import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddNewIcon3,
  CloseIcon,
  DeleteBin6LineIcon,
  EditLineIcon,
  InformationFillIcon,
  RotationLock2,
  UploadIcon,
} from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { downloadFile, getFileName, handleApiError, notify } from '~/utils/helper';
import { IAccount, IEditAccount } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import { ROLE_AGENT, ROLE_TECHINICIAN, ROLE_USER } from '~/utils/constants/account';
import { IMasterDataItem } from '~/utils/interface/master';
import utilityApi from '~/apis/utility';
import deviceApi from '~/apis/device';
import { IEquipment } from '~/utils/interface/equipment';
import { BaseScrollbar } from '~/components/common';
import facilityApi from '~/apis/facility';
import { IFacility } from '~/utils/interface/facility';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { useFormik } from 'formik';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { useDropzone } from 'react-dropzone';
import { commonActions } from '~/thunks/common/commonSlice';
import fileApi from '~/apis/file';
import SendPasswordDialog from '../send-password-dialog/SendPasswordDialog';
import DetailTechnicianDialog from '~/pages/admin/technicians/component/DetailTechnicianDialog';
import EditTechnicianDialog from '~/pages/admin/technicians/component/EditTechnicianDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import EditUserDialog from '~/pages/admin/users/component/edit-user-dialog/EditUserDialog';
import ShowUserDialog from '~/pages/admin/users/component/show-user-dialog/ShowUserDialog';
import equipmentApi from '~/apis/equipment';
import EditEquipmentDialog from '~/pages/admin/equipment/component/edit-equipment-dialog/EditEquipmentDialog';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import EditFacilityDialog from '~/pages/admin/facilities/components/edit-facility-dialog/EditFacilityDialog';
import DetailFacilityDialog from '~/pages/admin/facilities/components/detail-facility-dialog/DetailFacilityDialog';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useLocation } from 'react-router-dom';
import { handleGetAuthUserInfo } from '~/thunks/auth/authThunk';

type DetailEquipmentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IAccount;
  mode?: string;
  parent_username?: string;
};

type StatusColor = { [key: string]: string };
const statusColors: StatusColor = {
  0: '#72777B',
  1: '#3D9149',
  2: '#FF9500',
  3: '#F32A4C',
};

const initialValues: IEditAccount = {
  parent_username: '',
  contract_id: '',
  created_at: '',
  name: '',
  address: '',
  representative: '',
  phone: '',
  email: '',
  username: '',
  files: [],
  agent_level: 0,
};

const DetailAgentDialog = (props: DetailEquipmentDialogProps) => {
  const { t } = useTranslation(['admin/agent', 'admin/common']);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { authUser } = useAppSelector((state) => state.auth);

  const [agent, setAgent] = useState<IAccount>();
  const [agentLevelOptions, setAgentLevelOptions] = useState<IMasterDataItem[]>([]);
  const [facilityList, setFacilityList] = useState<IFacility[]>([]);
  const [technicians, setTechnicians] = useState<IAccount[]>();
  const [users, setUsers] = useState<IAccount[]>();
  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [distributorOptions, setDistributorOptions] = useState<IMasterDataItem[]>([]);
  const isSendEmail = useRef<boolean>(false);
  const [openSendPasswordDialog, setOpenSendPasswordDialog] = useState<boolean>(false);

  const [isOpenEditFacilityDialog, setIsOpenEditFacilityDialog] = useState<boolean>(false);
  const [isOpenDetailFacilityDialog, setIsOpenDetailFacilityDialog] = useState<boolean>(false);
  const [facility, setFacility] = useState<IFacility>();

  const [isOpenDetailTechnicianDialog, setIsOpenDetailTechnicianDialog] = useState<boolean>(false);
  const [isOpenUpdateTechnicianDialog, setIsOpenUpdateTechnicianDialog] = useState<boolean>(false);
  const modeTechnician = useRef<string>();

  const [isOpenDetailUserDialog, setIsOpenDetailUserDialog] = useState<boolean>(false);
  const [isOpenEditUserDialog, setIsOpenEditUserDialog] = useState<boolean>(false);

  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [isOpenEditEquipmentDialog, setIsOpenEditEquipmentDialog] = useState<boolean>(false);

  const modeEquipment = useRef<string>();

  const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState<boolean>(false);
  const dataDetail = useRef<any>();
  const roleType = useRef<string>();

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        dispatch(commonActions.setIsLoading(true));
        try {
          const { username, ...createValue } = values; // remove username out of form
          const response = await accountApi.createByRole({ role: ROLE_AGENT }, createValue);
          notify('success', t('saveSuccess', { ns: 'admin/common' }));
          if (isSendEmail.current) {
            setAgent(response.data);
            setOpenSendPasswordDialog(true);
          } else {
            handleSetOpen(false, true);
          }
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await accountApi.updateByRole(values);
          notify('success', t('saveSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }
    },
  });

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: async (acceptedFiles: Array<File>) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', acceptedFiles[0]);

        const response = await fileApi.uploadFiles(uploadForm);
        if (response.data) {
          form.setFieldValue('files', [...form.values.files, response.data.url]);
        }
      } catch (e: any) {
        handleApiError(e);
      }

      dispatch(commonActions.setIsLoading(false));
    },
  });

  const [equipmentFilter, setEquipmentFilter] = useState<number>(-1);
  const [facilityFilter, setFacilityFilter] = useState<number>(-1);

  useEffect(() => {
    if (props.open && props.data && (props.mode === DIALOG_MODE_EDIT || props.mode === DIALOG_MODE_VIEW)) {
      (async () => {
        try {
          const response = await accountApi.getByRoleList(ROLE_AGENT, { username: props.data?.username });
          if (response.data?.data?.length) {
            const data = response.data.data[0];
            setAgent(data);

            if (props.mode === DIALOG_MODE_EDIT) {
              form.setValues({
                parent_username: data.parent_username,
                contract_id: data.contract_id,
                created_at: data.created_at,
                name: data.name,
                address: data.address,
                representative: data.representative,
                phone: data.phone,
                email: data.email,
                username: data.username,
                files: data.files,
                agent_level: String(data.agent_level),
              });
            }
          }
        } catch (error) {
          handleApiError(error);
        }
      })();
      handleGetListFacility();

      handleGetTechnicians();

      handleGetUsers();

      (async () => {
        try {
          const response = await utilityApi.getAgentLevels();
          let agentLevelOptions: any = response.data.filter(
            (item) => item.level_id === 1 || item.level_id === Number(props.data?.agent_level)
          );
          if (Number(props.data?.agent_level) === 1) {
            agentLevelOptions.push({ level_id: 2, level_name: t('auto') });
          }
          setAgentLevelOptions(
            agentLevelOptions.map((item: any) => {
              return {
                id: String(item.level_id),
                name: item.level_name,
                data0: item.level_name,
              };
            })
          );
        } catch (error) {
          handleApiError(error);
        }
      })();

      handleGetDevices();
    }

    if (props.open) {
      dispatch(handleGetAuthUserInfo());
      (async () => {
        try {
          const response = await utilityApi.getListDistributors();
          setDistributorOptions(
            response.data.map((item) => {
              return {
                id: item.username,
                name: item.username + ' - ' + item.name,
                data0: item.name,
              };
            })
          );

          if (
            authUser?.permissions?.[0] === 'distributor' &&
            location.pathname.indexOf('/admin/agents') > -1 &&
            props.mode === DIALOG_MODE_CREATE
          ) {
            form.setFieldValue('parent_username', response.data?.[0].username);
          }

          if (props.mode === DIALOG_MODE_CREATE && props?.parent_username) {
            form.setFieldValue('parent_username', props.parent_username);
          }
        } catch (error) {
          handleApiError(error);
        }
      })();

      setEquipmentFilter(-1);
      setFacilityFilter(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode]);

  const handleGetDevices = async () => {
    try {
      const res = await deviceApi.getByUserList(String(props?.data?.username));
      setEquipmentList(res.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetTechnicians = async () => {
    try {
      const response = await accountApi.getByRole({ role: ROLE_TECHINICIAN, username: props.data?.username });
      setTechnicians(response.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetUsers = async () => {
    try {
      const response = await accountApi.getByRole({ role: ROLE_USER, username: props.data?.username });
      setUsers(response.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    isSendEmail.current = false;

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getStatus = (status: number | undefined) => {
    const color = statusColors[String(status) as keyof StatusColor];
    return (
      <Box className={`text-[${color}] border border-[${color}] rounded-[20px] px-[5px] py-[2px]`}>
        {t(`simStatuses.${status}`)}
      </Box>
    );
  };

  const handleDelete = async () => {
    try {
      if (dataDetail.current && roleType.current) {
        if (roleType.current === 'equipment') {
          await equipmentApi.deleteDevice(dataDetail.current?.device_id);
        } else {
          await accountApi.deleteByRole({
            username: dataDetail.current.username,
            role: roleType.current,
          });
        }

        if (roleType.current === ROLE_TECHINICIAN) {
          handleGetTechnicians();
        }
        if (roleType.current === ROLE_USER) {
          handleGetUsers();
        }
        if (roleType.current === 'equipment') {
          handleGetDevices();
        }

        notify('success', t(`deleteDialog.${roleType.current}.deleteSuccess`));
        setIsOpenDeleteConfirm(false);
      }
    } catch (error) {
      handleApiError(error);
      setIsOpenDeleteConfirm(false);
    }
  };

  const getEquipmentDetail = async (row: IEquipment) => {
    try {
      const res = await equipmentApi.getOverviewData({
        device_code: row.device_code,
      });
      return res.data.devices[0];
    } catch (error) {
      handleApiError(error);
      return {};
    }
  };

  const handleGetListFacility = async () => {
    try {
      const res = await facilityApi.getListFacilityByUser(String(props?.data?.username));
      setFacilityList(res.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetIsOpenEditFacilitytDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditFacilityDialog(isOpen);
    if (needRefresh) {
      handleGetListFacility();
    }
  };

  const getAgentLevelColor = (agentLevel: number) => {
    if (agentLevel === 1) return 'border-[#72777B] text-[#72777B]';
    if (agentLevel === 2) return 'border-[#FF9500] text-[#FF9500]';
    if (agentLevel === 3) return 'border-[#3D9149] text-[#3D9149]';
    return 'border-[#005F73] text-[#005F73]';
  };
  const getAgentLevel = (agentLevel: number) => {
    const agentLevelName = agentLevelOptions?.find((item) => Number(item.id) === Number(agentLevel))?.name || '';

    return (
      <Box className={`border rounded-[20px] p-1 text-center ${getAgentLevelColor(agentLevel)}`}>{agentLevelName}</Box>
    );
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => handleSetOpen(false)}
        fullWidth
        maxWidth={props?.mode === DIALOG_MODE_CREATE ? 'md' : 'xl'}
      >
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_CREATE ? (
            t('detailAgentDialog.createTitle')
          ) : props.mode === DIALOG_MODE_EDIT ? (
            <Box className='flex gap-[25px]'>
              {t('detailAgentDialog.editTitle')}
              <RotationLock2 className='cursor-pointer' onClick={() => setOpenSendPasswordDialog(true)} />
            </Box>
          ) : (
            t('detailAgentDialog.title')
          )}

          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow>
            <Box
              className={`${
                props.mode === DIALOG_MODE_CREATE
                  ? 'w-full'
                  : 'w-1/2 border-r border-solid border-[#DFDFDF] flex flex-col'
              } ${props.mode === DIALOG_MODE_EDIT ? 'pr-[42px]' : ''} relative `}
            >
              {props.mode === DIALOG_MODE_CREATE || props.mode === DIALOG_MODE_EDIT ? (
                <>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-1' : 'order-6'
                    }`}
                  >
                    <InputLabel className='w-1/2'>{t('detailAgentDialog.distributor')}:</InputLabel>
                    <Box className='w-1/2'>
                      <BaseSelect
                        size='medium'
                        options={distributorOptions}
                        value={form.values.parent_username}
                        placeholder={t('select')}
                        name='parent_username'
                        isSearchEnable
                        onChange={form.handleChange}
                        error={Boolean(form.errors?.parent_username)}
                        helperText={form.errors?.parent_username}
                        displayInvalidValue
                      />
                    </Box>
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-2' : 'order-9'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.contractId')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='contract_id'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.contract_id}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.contract_id)}
                      helperText={form.errors?.contract_id}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-3' : 'order-10'
                    }`}
                  >
                    <InputLabel className='w-1/2'>{t('detailAgentDialog.createdAt')}:</InputLabel>
                    <Box className='w-1/2'>
                      <BaseDatePicker
                        className='w-full'
                        portalId='datePicker-portalId'
                        value={form.values.created_at}
                        dateFormat='YYYY-MM-DD'
                        onChange={(date) => {
                          form.setFieldValue('expire_date_to', dayjs(date).format('YYYY-MM-DD'));
                        }}
                        placeholder={t('select', { ns: 'admin/common' })}
                      />
                    </Box>
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-4' : 'order-1'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.name')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='name'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.name}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.name)}
                      helperText={form.errors?.name}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-5' : 'order-2'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.address')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='address'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.address}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.address)}
                      helperText={form.errors?.address}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-6' : 'order-3'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.representative')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='representative'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.representative}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.representative)}
                      helperText={form.errors?.representative}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-7' : 'order-4'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.phone')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='phone'
                      placeholder={t('detailAgentDialog.enterPhone')}
                      value={form.values?.phone}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.phone)}
                      helperText={form.errors?.phone}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-[20px] mb-[20px] !items-center ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-8' : 'order-5'
                    }`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('detailAgentDialog.email')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='email'
                      placeholder={t('detailAgentDialog.enterEmail')}
                      value={form.values?.email}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.email)}
                      helperText={form.errors?.email}
                    />
                  </BaseRow>

                  <BaseRow
                    className={`gap-[20px] !items-start !justify-between ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-10' : 'order-11'
                    }`}
                  >
                    <Box className='w-1/2 font-bold'>
                      <InputLabel>{t('detailAgentDialog.files')}:</InputLabel>
                      <Box>
                        {form.values.files?.length
                          ? form.values.files.map((file, index) => (
                              <Box className='flex flex-row gap-[10px]'>
                                <Button
                                  className='block font-normal p-0 h-auto truncate'
                                  key={index}
                                  onClick={() => downloadFile(file)}
                                >
                                  • {getFileName(file)}
                                </Button>
                                <IconButton
                                  onClick={() => {
                                    form.setFieldValue(
                                      'files',
                                      form.values.files.filter((_, fileIndex) => fileIndex !== index)
                                    );
                                  }}
                                >
                                  <DeleteBin6LineIcon fill='#ED1002' />
                                </IconButton>
                              </Box>
                            ))
                          : ''}
                      </Box>
                    </Box>
                    <Box>
                      <Box className='mb-[7px]'>
                        <Box {...getRootProps()}>
                          <Box component='input' {...getInputProps()} />
                          <Box className='flex w-[216px] items-center justify-center rounded-[8px] p-[7px] border border-[#C6C6C6] cursor-pointer'>
                            <UploadIcon className='w-[20px] h-[20px] mr-auto' />
                            <Box className='mr-auto'>{t('selectFile', { ns: 'admin/common' })}</Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box className='text-[11px] w-[216px] text-center'>
                        {t('dragOrDropFile', { ns: 'admin/common' })}
                      </Box>
                    </Box>
                  </BaseRow>
                  {props.mode === DIALOG_MODE_EDIT ? (
                    <BaseRow className={`gap-[20px] mb-[20px] !items-center order-8`}>
                      <InputLabel className='w-1/2'>{t('detailAgentDialog.agentLevel')}:</InputLabel>
                      <Box className='w-1/2'>
                        <BaseSelect
                          size='medium'
                          options={agentLevelOptions}
                          value={form.values.agent_level}
                          placeholder={t('select')}
                          name='agent_level'
                          isSearchEnable
                          onChange={form.handleChange}
                          error={Boolean(form.errors?.agent_level)}
                          helperText={form.errors?.agent_level}
                          displayInvalidValue
                        />
                      </Box>
                    </BaseRow>
                  ) : null}
                </>
              ) : null}

              {props.mode === DIALOG_MODE_VIEW ? (
                <>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.name')}:</Box>
                    <Box>{agent?.name}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.address')}:</Box>
                    <Box>{agent?.address}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.representative')}:</Box>
                    <Box>{agent?.representative}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.phone')}:</Box>
                    <Box>{agent?.phone}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.email')}:</Box>
                    <Box>{agent?.email}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.distributor')}:</Box>
                    <Box>
                      {agent?.parent_username
                        ? distributorOptions.find(({ id }) => id === agent?.parent_username)?.name ||
                          agent?.parent_username
                        : ''}
                    </Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.username')}:</Box>
                    <Box>{agent?.username}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px] items-center'>
                    <Box className='font-bold'>{t('detailAgentDialog.agentLevel')}:</Box>
                    <Box className='w-[131px]'>{getAgentLevel(Number(agent?.agent_level))}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.contractId')}:</Box>
                    <Box>{agent?.contract_id}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.createdAt')}:</Box>
                    <Box>{agent?.created_at ? dayjs(agent.created_at).format('DD/MM/YYYY') : ''}</Box>
                  </Box>
                  <Box className='flex flex-col gap-[10px] mb-[16px]'>
                    <Box className='font-bold'>{t('detailAgentDialog.files')}:</Box>
                    <Box>
                      {agent?.files?.length
                        ? agent.files.map((file) => (
                            <Button className='block font-normal p-0 h-auto' onClick={() => downloadFile(file)}>
                              • {getFileName(file)}
                            </Button>
                          ))
                        : ''}
                    </Box>
                  </Box>
                </>
              ) : null}
              {props.mode === DIALOG_MODE_VIEW || props.mode === DIALOG_MODE_EDIT ? (
                <>
                  <Divider
                    className=' order-last'
                    sx={{
                      borderColor: '#DFDFDF',
                      borderBottomWidth: 1,
                      width: '100%',
                      mt: 3,
                      mb: 2,
                      height: '100%',
                    }}
                  ></Divider>
                  <Box className='mb-[36px] pr-[42px] order-last'>
                    <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                      <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailAgentDialog.facilityList')}</Box>
                      <Box className='flex gap-[6.5px]'>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
                          {facilityList?.length}
                        </Box>
                        {props.mode === DIALOG_MODE_EDIT && (
                          <AddNewIcon3
                            className='cursor-pointer ml-auto'
                            onClick={() => {
                              setIsOpenEditFacilityDialog(true);
                            }}
                          />
                        )}
                      </Box>
                    </BaseRow>
                    {props.mode === DIALOG_MODE_VIEW && (
                      <BaseRow className='mb-3 gap-[8px] text-[13px]'>
                        <Button
                          variant={facilityFilter === -1 ? 'contained' : 'outlined'}
                          className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#005F73] hover:border-[#005F73] hover:text-white hover:bg-[#005F73] ${
                            facilityFilter === -1 ? 'text-white bg-[#005F73]' : ' text-[#005F73] border-[#005F73]'
                          }`}
                          onClick={() => setFacilityFilter(-1)}
                        >
                          {t('facilityStatus.all')}
                        </Button>
                        <Button
                          variant={facilityFilter === 0 ? 'contained' : 'outlined'}
                          className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#72777B] hover:border-[#72777B] hover:text-white hover:bg-[#72777B] ${
                            facilityFilter === 0 ? 'text-white bg-[#72777B]' : ' text-[#72777B] border-[#72777B]'
                          }`}
                          onClick={() => setFacilityFilter(0)}
                        >
                          {t('facilityStatus.pending')} (
                          {facilityList?.filter((item) => item.deployment_status === 0)?.length})
                        </Button>
                        <Button
                          variant={facilityFilter === 1 ? 'contained' : 'outlined'}
                          className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#FF9500] hover:border-[#FF9500] hover:text-white hover:bg-[#FF9500] ${
                            facilityFilter === 1 ? 'text-white bg-[#FF9500]' : ' text-[#FF9500] border-[#FF9500]'
                          }`}
                          onClick={() => setFacilityFilter(1)}
                        >
                          {t('facilityStatus.deployed')} (
                          {facilityList?.filter((item) => item.deployment_status === 1)?.length})
                        </Button>
                        <Button
                          variant={facilityFilter === 2 ? 'contained' : 'outlined'}
                          className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#3D9149] hover:border-[#3D9149] hover:text-white hover:bg-[#3D9149] ${
                            facilityFilter === 2 ? 'text-white bg-[#3D9149]' : ' text-[#3D9149] border-[#3D9149]'
                          }`}
                          onClick={() => setFacilityFilter(2)}
                        >
                          {t('facilityStatus.activated')} (
                          {facilityList?.filter((item) => item.deployment_status === 2)?.length})
                        </Button>
                      </BaseRow>
                    )}
                    <BaseScrollbar maxHeight='250px'>
                      {facilityList?.length ? (
                        <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                          <Table>
                            <TableBody className='divide-y divide-[#005F73]'>
                              {facilityList
                                .filter((item) => {
                                  if (facilityFilter === -1) return true;
                                  return item.deployment_status === facilityFilter;
                                })
                                .map((row, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell className='border-none flex'>
                                        <Box className='font-bold'>{row.location_name}</Box>
                                        <Box>{row.address}</Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box className='font-bold'>{row.address}</Box>
                                        <Box className='flex flex-row gap-[4px] justify-end'></Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box className='mb-[5px]'>
                                          <Box component='span' className='font-bold text-[#ED1002]'>
                                            {row.device_count}
                                          </Box>{' '}
                                          {t('detailAgentDialog.device')}
                                        </Box>
                                        <Box className='font-bold'>
                                          {row.deployment_status === 2 ? (
                                            <Box className='text-[#3D9149]'>{t('facilityStatus.activated')}</Box>
                                          ) : row.deployment_status === 1 ? (
                                            <Box className='text-[#FF9500]'>{t('facilityStatus.deployed')}</Box>
                                          ) : (
                                            <Box className='text-[#72777B]'>{t('facilityStatus.pending')}</Box>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell className='border-none w-[80px]'>
                                        <IconButton
                                          onClick={() => {
                                            setFacility(row);
                                            setIsOpenDetailFacilityDialog(true);
                                          }}
                                        >
                                          <InformationFillIcon fill='#D9D9D9' />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : null}
                    </BaseScrollbar>
                  </Box>
                </>
              ) : null}
            </Box>

            {props.mode === DIALOG_MODE_VIEW || props.mode === DIALOG_MODE_EDIT ? (
              <Box className='w-1/2 ml-[24px]'>
                <Box className='mb-[36px]'>
                  <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                    <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailAgentDialog.technicianList')}</Box>
                    <Box className='flex gap-[6.5px]'>
                      <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
                        {technicians?.length}
                      </Box>
                      {props.mode === DIALOG_MODE_EDIT && (
                        <AddNewIcon3
                          className='cursor-pointer ml-auto'
                          onClick={() => {
                            modeTechnician.current = DIALOG_MODE_CREATE;
                            setIsOpenUpdateTechnicianDialog(true);
                          }}
                        />
                      )}
                    </Box>
                  </BaseRow>
                  <BaseScrollbar maxHeight='300px'>
                    {technicians?.length ? (
                      <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                        <Table>
                          <TableBody className='divide-y divide-[#005F73]'>
                            {technicians.map((row, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell className='border-none'>
                                    <Box>
                                      <Box>{row.name}</Box>
                                      <Box className='font-bold mb-[5px]'>{row.phone}</Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell className='border-none text-right'>
                                    <Box className='mb-[5px]'>
                                      <Box component='span' className='font-bold text-[#ED1002]'>
                                        {row.total_devices}
                                      </Box>{' '}
                                      {t('detailAgentDialog.device')}
                                    </Box>
                                  </TableCell>
                                  <TableCell className='border-none w-[80px]'>
                                    <Box className='flex'>
                                      <IconButton
                                        onClick={() => {
                                          dataDetail.current = row;
                                          setIsOpenDetailTechnicianDialog(true);
                                        }}
                                      >
                                        <InformationFillIcon fill='#D9D9D9' />
                                      </IconButton>
                                      {props.mode === DIALOG_MODE_EDIT ? (
                                        <>
                                          <IconButton
                                            onClick={() => {
                                              dataDetail.current = row;
                                              modeTechnician.current = DIALOG_MODE_EDIT;
                                              setIsOpenUpdateTechnicianDialog(true);
                                            }}
                                          >
                                            <EditLineIcon className='!w-[18px]' />
                                          </IconButton>
                                          <IconButton
                                            onClick={() => {
                                              dataDetail.current = row;
                                              roleType.current = ROLE_TECHINICIAN;
                                              setIsOpenDeleteConfirm(true);
                                            }}
                                          >
                                            <DeleteBin6LineIcon fill='#ED1002' className='!w-[18px]' />
                                          </IconButton>
                                        </>
                                      ) : null}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : null}
                  </BaseScrollbar>
                </Box>
                <Box className='mb-[36px]'>
                  <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                    <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailAgentDialog.userList')}</Box>
                    <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                      {users?.length}
                    </Box>{' '}
                  </BaseRow>
                  {users?.length ? (
                    <BaseScrollbar maxHeight='300px'>
                      <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                        <Table>
                          <TableBody className='divide-y divide-[#005F73]'>
                            {users.map((row, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell className='border-none'>
                                    <Box>
                                      <Box className='font-bold'>{row.name}</Box>
                                      <Box className='mb-[5px]'>{row.phone}</Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell className='border-none text-right'>
                                    <Box className='mb-[5px]'>
                                      <Box component='span' className='font-bold text-[#ED1002]'>
                                        {row.total_devices}
                                      </Box>{' '}
                                      {t('detailAgentDialog.device')}
                                    </Box>
                                  </TableCell>
                                  <TableCell className='border-none w-[80px]'>
                                    <Box className='flex'>
                                      <IconButton
                                        onClick={() => {
                                          dataDetail.current = row;
                                          roleType.current = ROLE_USER;
                                          setIsOpenDetailUserDialog(true);
                                        }}
                                      >
                                        <InformationFillIcon fill='#D9D9D9' />
                                      </IconButton>
                                      {props.mode === DIALOG_MODE_EDIT ? (
                                        <>
                                          <IconButton
                                            onClick={() => {
                                              dataDetail.current = row;
                                              roleType.current = ROLE_USER;
                                              setIsOpenEditUserDialog(true);
                                            }}
                                          >
                                            <EditLineIcon className='!w-[18px]' />
                                          </IconButton>
                                          <IconButton
                                            onClick={() => {
                                              dataDetail.current = row;
                                              roleType.current = ROLE_USER;
                                              setIsOpenDeleteConfirm(true);
                                            }}
                                          >
                                            <DeleteBin6LineIcon fill='#ED1002' className='!w-[18px]' />
                                          </IconButton>
                                        </>
                                      ) : null}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </BaseScrollbar>
                  ) : null}
                </Box>
                <Box className='mb-[36px]'>
                  <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                    <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailAgentDialog.equipmentList')}</Box>
                    <Box className='flex gap-[10px]'>
                      <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                        {equipmentList?.length}
                      </Box>
                      {props.mode === DIALOG_MODE_EDIT && (
                        <AddNewIcon3
                          className='cursor-pointer'
                          onClick={() => {
                            dataDetail.current = undefined;
                            roleType.current = 'equipment';
                            modeEquipment.current = DIALOG_MODE_CREATE;
                            setIsOpenEditEquipmentDialog(true);
                          }}
                        />
                      )}
                    </Box>
                  </BaseRow>
                  <BaseRow className='mb-3 gap-[8px] text-[13px]'>
                    <Button
                      variant={equipmentFilter === -1 ? 'contained' : 'outlined'}
                      className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#005F73] hover:border-[#005F73] hover:text-white hover:bg-[#005F73] ${
                        equipmentFilter === -1 ? 'text-white bg-[#005F73]' : ' text-[#005F73] border-[#005F73]'
                      }`}
                      onClick={() => setEquipmentFilter(-1)}
                    >
                      {t('deviceStatus.all')}
                    </Button>
                    <Button
                      variant={equipmentFilter === 1 ? 'contained' : 'outlined'}
                      className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#ED1002] hover:border-[#ED1002] hover:text-white hover:bg-[#ED1002]  ${
                        equipmentFilter === 1 ? 'text-white bg-[#ED1002]' : ' text-[#ED1002] border-[#ED1002]'
                      }`}
                      onClick={() => setEquipmentFilter(1)}
                    >
                      {t('deviceStatus.1')} ({equipmentList?.filter((item) => item.device_status === 1)?.length})
                    </Button>
                    <Button
                      variant={equipmentFilter === 0 ? 'contained' : 'outlined'}
                      className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#3D9149] hover:border-[#3D9149] hover:text-white hover:bg-[#3D9149] ${
                        equipmentFilter === 0 ? 'text-white bg-[#3D9149]' : 'border text-[#3D9149] border-[#3D9149] '
                      }`}
                      onClick={() => setEquipmentFilter(0)}
                    >
                      {t('deviceStatus.0')} ({equipmentList?.filter((item) => item.device_status === 0)?.length})
                    </Button>
                  </BaseRow>
                  <BaseScrollbar maxHeight='300px'>
                    {equipmentList?.length ? (
                      <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                        <Table>
                          <TableBody className='divide-y divide-[#005F73]'>
                            {equipmentList
                              .filter((item) => {
                                if (equipmentFilter === -1) return true;
                                return item.device_status === equipmentFilter;
                              })
                              .map((row, index) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell className='border-none'>
                                      <Box className='font-bold'>{row.device_code}</Box>
                                      <Box>{row.address}</Box>
                                    </TableCell>
                                    <TableCell className='border-none text-right'>
                                      <Box className='font-bold'>{row.device_name}</Box>
                                      <Box className='flex flex-row gap-[4px] justify-end'>
                                        {getStatus(row.sim_status)} {getStatus(row.iot_status)}
                                      </Box>
                                    </TableCell>
                                    <TableCell className='border-none w-[80px]'>
                                      <Box className='flex'>
                                        <IconButton
                                          onClick={() => {
                                            (async () => {
                                              const equipment = await getEquipmentDetail(row);
                                              dataDetail.current = equipment;
                                              roleType.current = 'equipment';
                                              setIsOpenDetailEquipmentDialog(true);
                                            })();
                                          }}
                                        >
                                          <InformationFillIcon fill='#D9D9D9' />
                                        </IconButton>
                                        {props.mode === DIALOG_MODE_EDIT ? (
                                          <>
                                            <IconButton
                                              onClick={() => {
                                                dataDetail.current = row;
                                                roleType.current = 'equipment';
                                                modeEquipment.current = DIALOG_MODE_EDIT;
                                                setIsOpenEditEquipmentDialog(true);
                                              }}
                                            >
                                              <EditLineIcon className='!w-[18px]' />
                                            </IconButton>
                                            <IconButton
                                              onClick={() => {
                                                dataDetail.current = row;
                                                roleType.current = 'equipment';
                                                setIsOpenDeleteConfirm(true);
                                              }}
                                            >
                                              <DeleteBin6LineIcon fill='#ED1002' className='!w-[18px]' />
                                            </IconButton>
                                          </>
                                        ) : null}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : null}
                  </BaseScrollbar>
                </Box>
              </Box>
            ) : null}
          </BaseRow>
        </DialogContent>

        {props.mode === DIALOG_MODE_CREATE || props.mode === DIALOG_MODE_EDIT ? (
          <DialogActions>
            <Button
              className='bg-[#ED1002] text-white w-[125px] h-[35px] hover:bg-[#005F73]'
              onClick={() => {
                isSendEmail.current = false;
                form.handleSubmit();
              }}
            >
              {t('save', { ns: 'admin/common' })}
            </Button>
            {props.mode === DIALOG_MODE_CREATE ? (
              <Button
                className='bg-[#ED1002] text-white w-[130px] h-[35px] hover:bg-[#005F73]'
                onClick={() => {
                  if (!form.values.email) {
                    return handleApiError(t('detailAgentDialog.emailRequired'));
                  }
                  isSendEmail.current = true;
                  form.handleSubmit();
                }}
              >
                {t('saveAndSendEmail', { ns: 'admin/common' })}
              </Button>
            ) : null}
          </DialogActions>
        ) : null}
      </Dialog>
      <SendPasswordDialog open={openSendPasswordDialog} setOpen={() => handleSetOpen(false, true)} data={agent} />

      {isOpenEditFacilityDialog ? (
        <EditFacilityDialog
          open={isOpenEditFacilityDialog}
          mode={DIALOG_MODE_CREATE}
          setOpen={handleSetIsOpenEditFacilitytDialog}
        />
      ) : null}

      {isOpenDetailFacilityDialog ? (
        <DetailFacilityDialog
          open={isOpenDetailFacilityDialog}
          data={facility}
          setOpen={setIsOpenDetailFacilityDialog}
        />
      ) : null}

      {/* technician */}
      {isOpenDetailTechnicianDialog ? (
        <DetailTechnicianDialog
          open={isOpenDetailTechnicianDialog}
          setOpen={setIsOpenDetailTechnicianDialog}
          data={dataDetail.current}
        />
      ) : null}

      {isOpenUpdateTechnicianDialog ? (
        <EditTechnicianDialog
          open={isOpenUpdateTechnicianDialog}
          setOpen={(open) => {
            setIsOpenUpdateTechnicianDialog(open);
            handleGetTechnicians();
          }}
          data={dataDetail.current}
          parent_username={props.data?.username}
          mode={modeTechnician.current}
        />
      ) : null}

      {/* user */}
      {isOpenDetailUserDialog ? (
        <ShowUserDialog open={isOpenDetailUserDialog} setOpen={setIsOpenDetailUserDialog} user={dataDetail.current} />
      ) : null}

      {isOpenEditUserDialog ? (
        <EditUserDialog
          data={dataDetail.current}
          open={isOpenEditUserDialog}
          mode={DIALOG_MODE_EDIT}
          setOpen={(open) => {
            setIsOpenEditUserDialog(open);
            handleGetUsers();
          }}
        />
      ) : null}

      {/* equipment */}
      {isOpenEditEquipmentDialog ? (
        <EditEquipmentDialog
          open={isOpenEditEquipmentDialog}
          mode={modeEquipment.current ?? DIALOG_MODE_CREATE}
          setOpen={(open) => {
            setIsOpenEditEquipmentDialog(open);
            handleGetDevices();
          }}
          data={dataDetail.current}
          agent={props.data}
        />
      ) : null}
      {isOpenDetailEquipmentDialog ? (
        <DetailEquipmentDialog
          open={isOpenDetailEquipmentDialog}
          setOpen={setIsOpenDetailEquipmentDialog}
          data={dataDetail.current}
        />
      ) : null}

      <ConfirmDialog
        open={isOpenDeleteConfirm}
        title={t(`deleteDialog.${roleType.current}.title`)}
        description={t(`deleteDialog.${roleType.current}.description`, {
          username: roleType.current === 'equipment' ? dataDetail.current?.device_code : dataDetail.current?.username,
        })}
        onConfirm={() => handleDelete()}
        onCancel={() => setIsOpenDeleteConfirm(false)}
      />
    </>
  );
};

export default memo(DetailAgentDialog);
